/* Farpoint -- live stock state for product options
 * ================================================
 *
 * THE PROBLEM
 * An option value like "Draw tube length: 2 inch" is a real catalogue product
 * with real inventory. Nothing on the product page knew that. A customer could
 * pick a sold-out draw tube, pay for it, and we would find out at packing time.
 *
 * WHAT THIS DOES
 * Greys out any variant-backed option value that cannot currently be sold, adds
 * an "Out of stock" note beside it, and blocks Add to cart when a REQUIRED
 * option has no sellable value left.
 *
 * THE THREE RULES (Cary, 2026-08-05) -- these are Shopify's own, not ours:
 *   inventory not tracked        -> always sellable
 *   tracked, keep selling when out -> sellable at any quantity (backorder,
 *                                   shown completely normally, no note)
 *   tracked, stop selling when out -> needs stock on hand
 * `/products/<handle>.js` already encodes all three in its per-variant
 * `available` flag, so this file does not re-implement the policy logic. It
 * only asks.
 *
 * LIVE, NOT BAKED
 * Availability is fetched when the page loads rather than read from the
 * compiled spec. Stock moves; a spec published last Tuesday does not. The
 * compiled `available` flag is still used -- as the instant first paint before
 * the fetch resolves, and as the fallback if it never does.
 *
 * WHY A FETCH FAILURE NEVER HIDES ANYTHING
 * Add-on products on this store may be unpublished from the Online Store
 * channel while remaining perfectly sellable through the Cart Transform. Those
 * return 404 from /products/<handle>.js. Treating a failed fetch as "out of
 * stock" would grey out the entire add-on catalogue. So a failure leaves the
 * publish-time answer standing, and the publish-time answer defaults to
 * sellable. The bias is deliberate: showing a sold-out item costs one apologetic
 * email, hiding a sellable one costs the sale silently.
 *
 * WHY IT INJECTS INSTEAD OF BEING RENDERED IN LIQUID
 * Same reason as fp-option-preview.js: the compiled spec is already in the DOM
 * at [data-fp-spec], and every value already maps to a stable selector pair --
 * [data-fp-option="<key>"] plus an input whose value is the value key. This
 * reuses a mapping that is already load bearing. Purely additive: if nothing
 * matches, nothing changes and the options behave exactly as before.
 *
 * ASCII ONLY, DELIBERATELY. This file is hand-transported into the theme and
 * verified by md5; multi-byte characters in comments have caused repeated
 * few-byte drift that cost more to diagnose than the typography was worth.
 */
(function () {
  "use strict";

  window.FP_OPTION_STOCK_VERSION = "2026-08-05.3";

  var NOTE = "Out of stock";
  var CACHE = {};          // handle -> Promise< map of numeric variant id -> bool >
  var known = {};          // "optKey\u0000valueKey" -> bool, current best answer

  ready(function () {
    initProduct();
    initCart();
  });

  function initProduct() {
    var root = document.querySelector("[data-fp-options]");
    if (!root) return;

    var options = collectOptions();
    if (!options.length) return;

    injectStyles();

    // PASS 1 -- synchronous, from the compiled spec. Runs before any network
    // call so a value that was already sold out at publish time is greyed on
    // first paint rather than flickering from enabled to disabled.
    apply(root, options, false);

    // PASS 2 -- live. Refines pass 1 once the real numbers arrive.
    var handles = {};
    for (var i = 0; i < options.length; i++) {
      var vs = options[i].values || [];
      for (var j = 0; j < vs.length; j++) {
        if (vs[j] && vs[j].handle && vs[j].variantId) handles[vs[j].handle] = true;
      }
    }

    var names = Object.keys(handles);
    if (!names.length) return;

    for (var h = 0; h < names.length; h++) {
      // Re-apply after each response rather than waiting for all of them: one
      // slow product should not hold up the other six. apply() is idempotent --
      // it skips any value whose answer has not changed.
      load(names[h]).then(function () { apply(root, options, true); });
    }
  }

  /* ---------- reading the spec ---------- */

  // The product's own set plus any shop-wide sets, in the same order the
  // snippet renders them.
  function collectOptions() {
    var out = [];
    var own = parse(document.querySelector("[data-fp-spec]"));
    if (own && own.options && own.options.length) out = out.concat(own.options);

    var g = parse(document.querySelector("[data-fp-spec-global]"));
    if (g && g.sets && g.sets.length) {
      for (var i = 0; i < g.sets.length; i++) {
        if (g.sets[i] && g.sets[i].options && g.sets[i].options.length) {
          out = out.concat(g.sets[i].options);
        }
      }
    }
    return out;
  }

  function parse(el) {
    if (!el) return null;
    try {
      var raw = JSON.parse(el.textContent || "null");
      // The snippet emits `| json` on a metafield value, which can arrive as a
      // JSON string containing JSON.
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (e) {
      return null;
    }
  }

  /* ---------- live availability ---------- */

  function load(handle) {
    if (CACHE[handle]) return CACHE[handle];

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    // `job.__value` is what makes peek() possible: apply() needs a synchronous
    // read of an already-settled fetch, and a Promise has no way to hand one
    // over. Assigning it in the tail .then is safe because `job` is bound
    // before any microtask can run.
    var job = fetch(base + "products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })
      .then(function (r) {
        if (!r.ok) throw r;
        return r.json();
      })
      .then(function (prod) {
        var map = {};
        var vs = (prod && prod.variants) || [];
        for (var i = 0; i < vs.length; i++) map[String(vs[i].id)] = vs[i].available !== false;
        return map;
      })
      .catch(function () {
        // See the header: a failure must never be read as "out of stock".
        return null;
      })
      .then(function (map) {
        job.__value = map;
        return map;
      });

    CACHE[handle] = job;
    return job;
  }

  // Compiled spec carries "gid://shopify/ProductVariant/123"; the Ajax API
  // answers in bare numbers.
  function numericId(gid) {
    var m = /(\d+)\s*$/.exec(String(gid || ""));
    return m ? m[1] : "";
  }

  function isSellable(value, live) {
    if (!value) return true;
    // A value with no variant behind it is a flat surcharge -- nothing to
    // stock, always sellable.
    if (!value.variantId) return true;

    if (live && value.handle) {
      var map = peek(value.handle);
      if (map) {
        var id = numericId(value.variantId);
        if (Object.prototype.hasOwnProperty.call(map, id)) return map[id] !== false;
      }
    }
    // Publish-time answer. Absent (spec predates this feature) means sellable.
    return value.available !== false;
  }

  // Synchronous read of an already-settled fetch, so apply() stays simple.
  // Null means "no answer yet, or the fetch failed" -- both fall through to the
  // publish-time flag.
  function peek(handle) {
    var job = CACHE[handle];
    return (job && job.__value) || null;
  }

  /* ---------- applying it to the DOM ---------- */

  function apply(root, options, live) {
    for (var o = 0; o < options.length; o++) {
      var opt = options[o];
      if (!opt || !opt.values || !opt.values.length) continue;

      var group = root.querySelector('[data-fp-option="' + cssEscape(opt.key) + '"]');
      if (!group) continue;

      var sellableCount = 0;
      var considered = 0;

      for (var i = 0; i < opt.values.length; i++) {
        var v = opt.values[i];
        if (!v) continue;
        var ok = isSellable(v, live);
        if (v.variantId) {
          considered++;
          if (ok) sellableCount++;
        } else {
          sellableCount++; // surcharge-only values always count as choosable
          considered++;
        }

        var key = opt.key + "\u0000" + v.key;
        if (known[key] === ok) continue; // nothing changed for this value
        known[key] = ok;
        mark(group, opt, v, ok);
      }

      // A required option whose every value is gone cannot be answered, so the
      // product cannot be configured. Recorded on the group; gate() decides
      // what to do about Add to cart.
      var dead = considered > 0 && sellableCount === 0;
      group.setAttribute("data-fp-stock-dead", dead ? "true" : "false");
    }

    gate(root);
  }

  function mark(group, opt, value, ok) {
    var type = group.getAttribute("data-fp-type") || "";

    if (type === "dropdown") {
      var o = group.querySelector('option[value="' + cssEscape(value.key) + '"]');
      if (!o) return;
      o.disabled = !ok;
      o.textContent = strip(o.textContent) + (ok ? "" : " (" + NOTE + ")");
      if (!ok && o.selected) clearSelect(group, o);
      return;
    }

    if (type === "swatch") {
      var btn = group.querySelector('[data-fp-value="' + cssEscape(value.key) + '"]');
      if (!btn) return;
      btn.disabled = !ok;
      btn.classList.toggle("fp-oos", !ok);
      btn.setAttribute("title", (value.label || "") + (ok ? "" : " - " + NOTE));
      if (!ok && btn.getAttribute("aria-pressed") === "true") {
        btn.setAttribute("aria-pressed", "false");
        btn.classList.remove("is-selected");
        fire(btn);
      }
      return;
    }

    // radio / checkbox / checkbox_group
    var input = group.querySelector('input[value="' + cssEscape(value.key) + '"]');
    if (!input) return;
    var row = input.closest(".fp-option__checkbox, .fp-option__radio") || input.parentNode;
    var host = input.parentNode && input.parentNode.querySelector("span");

    input.disabled = !ok;
    if (row && row.classList) row.classList.toggle("fp-oos", !ok);

    var existing = host && host.querySelector("[data-fp-oos]");
    if (!ok && host && !existing) {
      var tag = document.createElement("span");
      tag.className = "fp-option__oos";
      tag.setAttribute("data-fp-oos", "");
      tag.textContent = NOTE;
      host.appendChild(document.createTextNode(" "));
      host.appendChild(tag);
    } else if (ok && existing) {
      existing.parentNode.removeChild(existing);
    }

    // Unticking is not cosmetic: leaving a sold-out value selected would send
    // its key in _fp_config and the Cart Transform would happily price it.
    if (!ok && input.checked) {
      input.checked = false;
      fire(input);
    }
  }

  function clearSelect(group, option) {
    var sel = option.parentNode;
    var replacement = null;
    for (var i = 0; i < sel.options.length; i++) {
      if (!sel.options[i].disabled) { replacement = sel.options[i]; break; }
    }
    sel.value = replacement ? replacement.value : "";
    fire(sel);
  }

  /* ---------- Add to cart ---------- */

  // Deliberately narrow. Only a REQUIRED option with nothing left to choose
  // blocks the sale -- an optional add-on going out of stock must never stand
  // between a customer and the product they actually came for.
  function gate(root) {
    var dead = [];
    var groups = root.querySelectorAll('[data-fp-stock-dead="true"]');
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.hasAttribute("hidden")) continue;                       // conditional, not shown
      if (g.getAttribute("data-fp-required") !== "true") continue;   // optional, let it go
      dead.push(g);
    }

    // Clear only the messages this file wrote, wherever they are -- a group
    // that has come back into stock is no longer in `groups`.
    var stale = root.querySelectorAll('[data-fp-stock-msg="true"]');
    for (var d = 0; d < stale.length; d++) {
      stale[d].hidden = true;
      stale[d].textContent = "";
      stale[d].removeAttribute("data-fp-stock-msg");
    }

    var form = root.closest('form[action*="/cart/add"]') ||
               document.querySelector('form[action*="/cart/add"]');
    var btns = form
      ? form.querySelectorAll('[name="add"], .js-add-to-cart, .add-to-cart, button[type="submit"]')
      : [];

    for (var b = 0; b < btns.length; b++) {
      if (dead.length) {
        btns[b].setAttribute("data-fp-stock-blocked", "true");
        btns[b].disabled = true;
      } else if (btns[b].getAttribute("data-fp-stock-blocked") === "true") {
        // Only re-enable what WE disabled. The theme disables this button for
        // its own reasons (sold-out parent, unselected variant) and undoing
        // that would sell something Shopify already refused.
        btns[b].removeAttribute("data-fp-stock-blocked");
        btns[b].disabled = false;
      }
    }

    for (var k = 0; k < dead.length; k++) {
      var e = dead[k].querySelector("[data-fp-error]");
      if (!e) continue;
      e.textContent =
        (dead[k].getAttribute("data-fp-label") || "This option") +
        " is out of stock, so this cannot be ordered right now.";
      e.hidden = false;
      e.setAttribute("data-fp-stock-msg", "true");
    }
  }

  /* ---------- plumbing ---------- */

  function fire(el) {
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Strips a previously appended " (Out of stock)" so repeated passes cannot
  // stack it.
  function strip(text) {
    return String(text || "").replace(new RegExp("\\s*\\(" + NOTE + "\\)\\s*$"), "");
  }

  function cssEscape(s) {
    s = String(s == null ? "" : s);
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return s.replace(/["\\]/g, "\\$&");
  }

  function injectStyles() {
    if (document.getElementById("fp-option-stock-styles")) return;
    var css = [
      ".fp-oos{opacity:.55}",
      ".fp-oos,.fp-oos *{cursor:not-allowed}",
      ".fp-option__checkbox.fp-oos:hover,.fp-option__radio.fp-oos:hover{background:transparent}",
      ".fp-option__oos{",
      "margin-left:.35rem;padding:0 .35rem;",
      "border:1px solid #b9bfd0;border-radius:3px;",
      "font-size:11px;font-weight:700;line-height:1.5;",
      "text-transform:uppercase;letter-spacing:.03em;",
      "color:#5f6478;background:#f4f5f9;white-space:nowrap;",
      "}",
      "button.fp-option__swatch.fp-oos{background:#f4f5f9}",
      "button.fp-option__swatch.fp-oos .fp-option__swatch-chip{filter:grayscale(1)}",
      ".fp-cart-stock{",
      "margin:0 0 12px;padding:10px 12px;",
      "border:1px solid #ba4444;border-left:5px solid #ba4444;border-radius:4px;",
      "background:#fdf3f3;color:#7a2727;",
      "font-size:14px;line-height:1.4;font-weight:600;",
      "}"
    ].join("");
    var el = document.createElement("style");
    el.id = "fp-option-stock-styles";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  /* ================================================================== *
   * THE CART
   * ==================================================================
   *
   * WHY THIS EXISTS
   * Everything above runs when the product page loads. It cannot help a cart
   * that was filled BEFORE the add-on sold out -- the customer picked an
   * in-stock adapter on Tuesday, left the tab open, and checks out on Thursday.
   * Nothing re-checked in between.
   *
   * Neither Function can cover this. The Cart and Checkout Validation Function's
   * ProductVariant type exposes no inventory fields at all -- no
   * availableForSale, no quantityAvailable, no inventoryItem (verified against
   * the generated schema, 2026-08-05). And the Cart Transform's input is scoped
   * to the cart, so it cannot look up an arbitrary variant either. A guard has
   * to live here.
   *
   * Client-side, and therefore bypassable by someone determined. That is
   * accepted: the case being defended against is a customer who simply does not
   * know, not an attacker.
   *
   * WHERE THE SPEC COMES FROM
   * The cart page has no compiled spec in its markup -- only the parent line and
   * its properties. `_fp_config` gives us the selection KEYS; turning those into
   * variant handles needs the spec. So the theme carries a tiny alternate
   * product view, templates/product.fp-spec.liquid, which renders the compiled
   * metafield and nothing else. One small JSON fetch per distinct product in the
   * cart, rather than scraping a 500 KB product page.
   */

  var SPECS = {};   // product handle -> Promise<spec|null>, with __value like load()
  var checking = false;

  function initCart() {
    // The drawer exists on every page, so this is not gated on the cart URL.
    if (!document.querySelector("[data-products], .cart__items, [name='checkout']")) return;

    // initProduct() returns early on the cart page, so the stylesheet has not
    // been added there. Idempotent.
    injectStyles();

    checkCart();
    ["cart:updated", "cart:build", "ajaxProduct:added", "CartDrawer:open"].forEach(function (name) {
      document.addEventListener(name, function () { setTimeout(checkCart, 250); });
    });
  }

  function checkCart() {
    if (checking) return;
    checking = true;

    getJson("cart.js")
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var jobs = [];
        for (var i = 0; i < items.length; i++) {
          var cfg = readConfig(items[i]);
          if (cfg) jobs.push(examine(items[i], cfg));
        }
        return Promise.all(jobs);
      })
      .then(function (results) {
        var gone = [];
        for (var i = 0; i < results.length; i++) {
          if (results[i] && results[i].length) gone = gone.concat(results[i]);
        }
        renderCartWarning(gone);
      })
      .catch(function () {
        // Never let a failure here block checkout. Same bias as everywhere else
        // in this file: silence, not a false stop.
        renderCartWarning([]);
      })
      .then(function () { checking = false; });
  }

  function readConfig(item) {
    var props = item && item.properties;
    if (!props || !props._fp_config) return null;
    try {
      var cfg = JSON.parse(props._fp_config);
      return cfg && cfg.sel && cfg.sel.length ? cfg : null;
    } catch (e) {
      return null;
    }
  }

  // Returns the labels of any selection on this line that can no longer be sold.
  function examine(item, cfg) {
    return spec(item.handle).then(function (sp) {
      if (!sp || !sp.options) return [];

      var byKey = {};
      for (var i = 0; i < sp.options.length; i++) byKey[sp.options[i].key] = sp.options[i];

      var wanted = [];
      for (var s = 0; s < cfg.sel.length; s++) {
        var opt = byKey[cfg.sel[s][0]];
        if (!opt || !opt.values) continue;
        for (var v = 0; v < opt.values.length; v++) {
          if (opt.values[v].key === cfg.sel[s][1] && opt.values[v].handle && opt.values[v].variantId) {
            wanted.push(opt.values[v]);
          }
        }
      }
      if (!wanted.length) return [];

      var loads = [];
      for (var w = 0; w < wanted.length; w++) loads.push(load(wanted[w].handle));

      return Promise.all(loads).then(function () {
        var gone = [];
        for (var k = 0; k < wanted.length; k++) {
          if (!isSellable(wanted[k], true)) gone.push(wanted[k].label || "an add-on");
        }
        return gone;
      });
    });
  }

  // templates/product.fp-spec.liquid -- the compiled metafield, nothing else.
  function spec(handle) {
    if (!handle) return Promise.resolve(null);
    if (SPECS[handle]) return SPECS[handle];

    var job = getJson("products/" + encodeURIComponent(handle) + "?view=fp-spec")
      .then(function (raw) {
        var out = typeof raw === "string" ? JSON.parse(raw) : raw;
        // Belt and braces for the Accept-header trap above: if we somehow got
        // the built-in product payload rather than our view, say so loudly in
        // the console rather than failing silently for a second time.
        if (out && out.product && !out.options) {
          if (window.console) {
            console.warn("[fp-option-stock] ?view=fp-spec returned the product payload, " +
                         "not the compiled spec. Stock checking is inactive for " + handle + ".");
          }
          return null;
        }
        return out;
      })
      .catch(function () { return null; });

    SPECS[handle] = job;
    return job;
  }

  // NO `Accept: application/json` HEADER. NOT AN OVERSIGHT.
  //
  // Sending it makes Shopify ignore `?view=fp-spec` entirely and return the
  // built-in product JSON instead -- `{"product":{...}}`, 9.4 KB of catalogue
  // data with no `options` array in our shape. `examine()` then finds nothing to
  // check and returns an empty list, so the guard silently does nothing at all.
  //
  // Verified live on 2026-08-05: same URL, no headers -> 6,322 bytes of compiled
  // spec, 9 options; with Accept: application/json -> the product payload. That
  // one header was the whole difference between working and quietly dead.
  function getJson(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(base + path, { credentials: "same-origin" })
      .then(function (r) {
        if (!r.ok) throw r;
        return r.json();
      });
  }

  function renderCartWarning(gone) {
    var existing = document.getElementById("fp-cart-stock");

    if (!gone.length) {
      if (existing) existing.parentNode.removeChild(existing);
      unblockCheckout();
      return;
    }

    // De-duplicate: the same add-on can sit on more than one configured line.
    var seen = {}, names = [];
    for (var i = 0; i < gone.length; i++) {
      if (seen[gone[i]]) continue;
      seen[gone[i]] = true;
      names.push(gone[i]);
    }

    var text =
      (names.length === 1 ? names[0] + " is" : names.join(", ") + " are") +
      " no longer in stock. Please remove " +
      (names.length === 1 ? "that item" : "those items") +
      " or change your selection before checking out.";

    var box = existing;
    if (!box) {
      box = document.createElement("div");
      box.id = "fp-cart-stock";
      box.className = "fp-cart-stock";
      box.setAttribute("role", "alert");
      var host = document.querySelector(".cart__items, [data-products]");
      if (!host || !host.parentNode) return;
      host.parentNode.insertBefore(box, host);
    }
    box.textContent = text;

    blockCheckout();
  }

  function checkoutButtons() {
    return document.querySelectorAll(
      "[name='checkout'], .cart__checkout, [href='/checkout'], .shopify-payment-button__button"
    );
  }

  function blockCheckout() {
    var btns = checkoutButtons();
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute("data-fp-cart-blocked", "true");
      btns[i].disabled = true;
      btns[i].setAttribute("aria-disabled", "true");
      btns[i].style.pointerEvents = "none";
      btns[i].style.opacity = "0.5";
    }
  }

  // Only ever undo what we did. Note the attribute differs from gate()'s
  // `data-fp-stock-blocked` on purpose: on a product page both guards can be
  // active at once, and sharing one marker would let the cart guard re-enable an
  // Add to cart button the product guard had deliberately disabled.
  function unblockCheckout() {
    var btns = document.querySelectorAll("[data-fp-cart-blocked='true']");
    for (var i = 0; i < btns.length; i++) {
      btns[i].removeAttribute("data-fp-cart-blocked");
      btns[i].disabled = false;
      btns[i].removeAttribute("aria-disabled");
      btns[i].style.pointerEvents = "";
      btns[i].style.opacity = "";
    }
  }
})();
